// Sign-in with the hub's Zitadel (authorization code + PKCE, public client
// "Type Builder" in the GimasiIoT Hub project), as Floor Plan Studio: the
// sign-in page asks for the e-mail and hands it to Zitadel's page as
// login_hint. The access token is a JWT for the hub project's audience;
// Zitadel only says who the person is - organisations and roles are the
// builder's own (GIOTHUB-186). The settings come from /api/v1/config.
import { UserManager, WebStorageStateStore } from "oidc-client-ts";

export interface OIDCConfig {
  issuer: string;
  client_id: string;
  project_id: string;
}

let um: UserManager | null = null;
let cfg: OIDCConfig | null = null;

export async function loadConfig(): Promise<OIDCConfig> {
  if (cfg) return cfg;
  const r = await fetch("/api/v1/config");
  if (!r.ok) throw new Error(`config: ${r.status}`);
  cfg = (await r.json()) as OIDCConfig;
  return cfg;
}

async function manager(): Promise<UserManager> {
  if (um) return um;
  const c = await loadConfig();
  if (!c.issuer || !c.client_id) throw new Error("sign-in is not configured on this builder");
  um = new UserManager({
    authority: c.issuer,
    client_id: c.client_id,
    redirect_uri: `${location.origin}/callback`,
    post_logout_redirect_uri: `${location.origin}/`,
    response_type: "code",
    scope: `openid profile email offline_access urn:zitadel:iam:org:project:id:${c.project_id}:aud`,
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });
  return um;
}

/** The bearer for API calls ("" = not signed in). */
export async function bearer(): Promise<string> {
  const u = await (await manager()).getUser();
  return u && !u.expired ? u.access_token : "";
}

export async function signIn(returnTo = location.pathname + location.search, email = "") {
  return (await manager()).signinRedirect({ login_hint: email || undefined, state: { returnTo } });
}

/** Signed in (a token that has not expired)? */
export async function signedIn(): Promise<boolean> {
  return (await bearer()) !== "";
}

/** On /callback: finish the sign-in, return where to go next. */
export async function completeSignIn(): Promise<string> {
  const u = await (await manager()).signinRedirectCallback();
  const st = u.state as { returnTo?: string } | undefined;
  return st?.returnTo && st.returnTo.startsWith("/") ? st.returnTo : "/";
}

export async function signOut() {
  await (await manager()).signoutRedirect();
}
